<template>
  <div>
    <h2 class="mb-10">
      Settings
    </h2>
    <div class="mb-5">
      Workspace can be published as a community page now! This will consolidate
      all your Community Published Collections on one page for easier navigation
      in the Community area and let other users know whose collections are these.
      Publish your workspace and check it out on the Community tab.
    </div>
    <v-row
      class="justify-space-between"
      no-gutters>
      <div class="font-weight-bold">
        {{ getActiveWorkspaceName }} Page
      </div>
      <div>
        <v-tooltip
          :disabled="canPublishWs.allowed"
          bottom>
          <template #activator="{ on: onTooltip }">
            <v-menu
              :disabled="!canPublishWs.allowed"
              offset-y
              offset-x
              nudge-right="40"
              nudge-bottom="10"
              close-on-content-click>
              <template #activator="{ on: onMenu }">
                <div
                  v-on="{...onTooltip, ...onMenu }">
                  <v-icon
                    medium
                    color="black"
                    class="pointer">
                    mdi-dots-vertical
                  </v-icon>
                </div>
              </template>
              <v-card
                class="nav-card py-0 disable-shadow">
                <p
                  :style="{color: !canPublishWs.allowed ? 'gray' : ''}"
                  @click="manageCreationPage">
                  {{ isActiveWorkspacePublished ? 'Unp' : 'P' }}ublish
                </p>
              </v-card>
            </v-menu>
          </template>
          <span v-if="!canPublishWs.allowed">
            {{ GET_TEXT_REASON(canPublishWs.reason) }}
          </span>
        </v-tooltip>
      </div>
    </v-row>

    <div class="mt-12">
      <h3 class="fs-16 mb-4">
        Synonyms/Sub Brands
        <span
          v-if="isSynonymsDirty || isSavingSynonyms"
          class="ml-1 text--secondary">
          {{ isSavingSynonyms ? 'saving...' : '(unsaved changes)' }}
        </span>
      </h3>
      <div style="max-width: 600px">
        <AppKeywordInput v-model="wsSynonyms" />
      </div>
      <div class="mt-3">
        <v-btn
          small
          color="primary"
          :disabled="isSavingSynonyms"
          @click="saveSynonyms">
          Save
        </v-btn>
        <v-btn
          small
          text
          :disabled="!isSynonymsDirty || isSavingSynonyms"
          @click="cancelSynonyms">
          Cancel
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapState, mapGetters, mapActions,
} from 'vuex';
import AppKeywordInput from '@/components/App/AppKeywordInput';
import WorkspaceApi from '@/services/graphql/workspace';
import { GET_TEXT_REASON } from '@/constants/userPermissions';

export default {
  name: 'Settings',
  components: {
    AppKeywordInput,
  },
  data() {
    return {
      wsSynonyms: [],
      originalSynonyms: [],
      isSavingSynonyms: false,
    };
  },
  computed: {
    ...mapState('Workspace', ['activeWorkspaceId']),
    ...mapGetters('Workspace', ['getActiveWorkspace', 'isActiveWorkspaceAllowPages', 'isActiveWorkspacePublished']),
    ...mapGetters('UserRoles', ['canPublishWs']),
    ...mapGetters('FeatureFlags', ['showUpdatesToWorkspaceGuests']),
    getActiveWorkspaceName() {
      const { name = '' } = this.getActiveWorkspace ?? {
      };
      return name;
    },
    isSynonymsDirty() {
      const a = new Set(this.wsSynonyms);
      const b = new Set(this.originalSynonyms);
      if (a.size !== b.size) return true;
      for (const x of a) {
        if (!b.has(x)) return true;
      }
      return false;
    },
  },
  watch: {
    getActiveWorkspace: {
      immediate: true,
      handler(workspace) {
        const synonyms = (workspace && workspace.synonyms) || [];
        this.wsSynonyms = [...synonyms];
        this.originalSynonyms = [...synonyms];
      },
    },
  },
  methods: {
    ...mapActions('Workspace', ['deleteWorkspacePage', 'createWorkspacePage', 'listWorkspacePages']),
    ...mapActions('MyPages', ['followPage']),
    GET_TEXT_REASON,
    async manageCreationPage() {
      if (!this.isActiveWorkspaceAllowPages) {
        return;
      }
      const workspaceId = this.activeWorkspaceId;
      if (this.isActiveWorkspacePublished) {
        this.deleteWorkspacePage({
          workspaceId,
          pageId: workspaceId, // in future it will be wrong if we will have a lot of ws pages in one ws
        });
        return;
      }
      await this.createWorkspacePage({
        workspaceId,
      });

      if (this.showUpdatesToWorkspaceGuests) {
        this.followPage({
          pageId: workspaceId,
          pageWorkspaceId: workspaceId,
        });
      }
    },

    async saveSynonyms() {
      if (!this.isSynonymsDirty) return;

      this.isSavingSynonyms = true;
      try {
        await WorkspaceApi.updateWorkspace({
          workspaceId: this.activeWorkspaceId,
          synonyms: this.wsSynonyms,
        });
        this.originalSynonyms = [...this.wsSynonyms];
      } catch (err) {
        console.error('Failed to save workspace synonyms', err);
      } finally {
        this.isSavingSynonyms = false;
      }
    },
    cancelSynonyms() {
      this.wsSynonyms = [...this.originalSynonyms];
    },
  },
};
</script>

<style scoped lang="scss">
</style>
